import { Checkbox, useTheme } from '@fluentui/react'
import { RootState } from 'app/store'
import LookupControl from 'components/controls/LookupControl'
import { EHideReason } from 'global/Visibility/EHideReason'
import { IVisibilityScope } from 'global/Visibility/IVisibilityScope.type'
import {
    bcbs_config_attribute as IXrmTypes_bcbs_config_attribute,
    bcbs_config_category as IXrmTypes_bcbs_config_category,
    bcbs_config_masterattribute as IXrmTypes_bcbs_config_masterattribute
} from 'model/CRM'
import {
    IDateValue as FAM_IDateValue,
    IFlexibleField as FAM_IFlexibleField,
    IGroupConfigurationConfig as FAM_IGroupConfigurationConfig,
    INumberValue as FAM_INumberValue,
    IStringValue as FAM_IStringValue,
    TScope as FAM_TScope,
    TValue as FAM_TValue
} from 'model/FlexibleAttributeModel'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import AttributeSetGridView from 'views/BlueRelationship/AttributeSetGridView'
import ComparisonAttributeSetGridView from 'views/Comparison/ComparisonAttributeSetGridView'
import ControlDebug from './controls/ControlDebug'
import Costshare from './controls/Costshare'
import Currency from './controls/Currency'
import DatePickerControl from './controls/DatePickerControl'
import InformationCallout from './controls/InformationCallout'
import MessageControl from './controls/MessageControl'
import MultiselectOptionsetControl from './controls/MultiselectOptionsetControl'
import MultiTextField from './controls/MultiTextField'
import OptionControl from './controls/OptionControl'
import OptionsetControl from './controls/OptionsetControl'
import PlaceHolderControl from './controls/PlaceHolderControl'
import SliderPercentControl from './controls/SliderPercentControl'
import TextFieldControl from './controls/TextFieldControl'
import WholeNumber, { EFormat } from './controls/WholeNumber'

export enum EControlType {
    boolean = 808900000,
    currency = 808900002,
    date = 808900003,
    decimal = 808900004,
    percent = 808900005,
    text = 808900008,
    optionset = 808900007,
    multiText = 808900009,
    wholeNumber = 808900010,
    message = 808900014,

    costshare = 808900001,
    visionCostshare = 808900012,
    pharmacyCostshare = 808900013,
    attributeSet = 808900015,
    lookup = 808900006,
    multiOptionset = 808900016,


    // retire the rest
    checkBox = 1,
    range = 6
}


interface IControlProps {
    [key: string]: any;
    label?: string;
    controlType: EControlType;
    disabled?: boolean;
    field: FAM_IFlexibleField;
    fieldValueOnChange?: (
        event: React.FormEvent<HTMLElement | HTMLInputElement>,
        value: FAM_TValue | FAM_IStringValue | FAM_INumberValue | FAM_IDateValue | undefined
    ) => void
    defaultFieldValue?: any;
    config: FAM_IGroupConfigurationConfig;
    locked?: boolean;

    // this bcbs_config_category, where defines the category of fields
    // TODO rename this bcbs_config_category;
    // WARN child elements get props and are not strongly typed
    category?: IXrmTypes_bcbs_config_category;

    costshareCategory?: IXrmTypes_bcbs_config_category;

    // this instance of bcbs_config_category, where defines fields, it also inherits from bcbs_config_category
    // TODO rename this categoryInstance or categoryData
    // WARN child elements get props and are not strongly typed
    scope?: FAM_TScope;

    masterAttribute?: IXrmTypes_bcbs_config_masterattribute;

    configAttribute?: IXrmTypes_bcbs_config_attribute;

    // TODO retire this
    // WARN child elements get props and are not strongly typed
    attribute?: IXrmTypes_bcbs_config_attribute;

    state: RootState;
    isHiddenReason?: EHideReason;
    comparison?: boolean | null;
    side?: 'left' | 'right' | 'merge' | undefined;
    visibility?: IVisibilityScope
}

interface ITextControlProps extends IControlProps {
    controlType: EControlType.text
    handleChange?: (event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => void;
}

interface IMultiTextControlProps extends IControlProps {
    controlType: EControlType.multiText
}

interface ICheckboxControlProps extends IControlProps {
    controlType: EControlType.checkBox
}

interface IOptionsetControlProps extends IControlProps {
    controlType: EControlType.optionset
}

interface IBooleanControlProps extends IControlProps {
    controlType: EControlType.boolean;
}

interface IDateControlProps extends IControlProps {
    controlType: EControlType.date;
}

interface IRangeProps extends IControlProps {
    controlType: EControlType.range;
    min: number;
    max: number;
}

interface IPercentControlProps extends IControlProps {
    controlType: EControlType.percent;
}

interface IMessageControlProps extends IControlProps {
    controlType: EControlType.message;
}
interface ICurrencyControlProps extends IControlProps {
    controlType: EControlType.currency;
}

interface IWholeNumberControlProps extends IControlProps {
    controlType: EControlType.wholeNumber;
}

interface IDecimalControlProps extends IControlProps {
    controlType: EControlType.decimal;
}

interface ICostshareControlProps extends IControlProps {
    controlType: EControlType.costshare;
}

interface IVisionCostshareControlProps extends IControlProps {
    controlType: EControlType.visionCostshare;
}

interface IPharmacyCostshareControlProps extends IControlProps {
    controlType: EControlType.pharmacyCostshare;
}

interface IAttributeSetControlProps extends IControlProps {
    controlType: EControlType.attributeSet;
}

interface ILookupControlProps extends IControlProps {
    controlType: EControlType.lookup;
}

interface IMultiOptionsetSetControlProps extends IControlProps {
    controlType: EControlType.multiOptionset;
}

type TControlWrapperTypes =
    ITextControlProps |
    IBooleanControlProps |
    ICheckboxControlProps |
    IOptionsetControlProps |
    IDateControlProps |
    IRangeProps |
    IPercentControlProps |
    IMessageControlProps |
    ICurrencyControlProps |
    IWholeNumberControlProps |
    IDecimalControlProps |
    IMultiTextControlProps |
    ICostshareControlProps |
    IVisionCostshareControlProps |
    IPharmacyCostshareControlProps |
    ILookupControlProps |
    IAttributeSetControlProps |
    IMultiOptionsetSetControlProps;


const styleHidden: React.CSSProperties = { opacity: '0.3' };
const fieldInformationCalloutContainerStyle: React.CSSProperties = { position: 'relative' };
const fieldInformationCalloutPositionStyle: React.CSSProperties = { position: 'absolute', left: '-24px', top: '26px' };
const highlightStyleBase: React.CSSProperties = {
    border: 'thin solid #fdecb2',
    padding: '6px',
    borderRadius: '6px'
};
const highlightBackgroundByThemeName: Record<string, string> = {
    default: '#fdecb2'
};
const highlightBackgroundFallback = '#655930';

interface IWrapperStyleArgs {
    comparison?: boolean | null;
    hasFieldVisibility: boolean;
    isHighlighted: boolean;
    themeName?: string;
}

const getWrapperStyle = ({ comparison, hasFieldVisibility, isHighlighted, themeName }: IWrapperStyleArgs): React.CSSProperties | undefined => {
    let nextStyle: React.CSSProperties | undefined;

    if (!comparison && hasFieldVisibility) {
        nextStyle = styleHidden;
    }

    if (isHighlighted) {
        nextStyle = {
            ...nextStyle,
            ...highlightStyleBase,
            backgroundColor: (themeName ? highlightBackgroundByThemeName[themeName] : undefined) ?? highlightBackgroundFallback
        };
    }

    return nextStyle;
};

type TControlRenderer = (props: TControlWrapperTypes) => JSX.Element;

const renderAttributeSetControl: TControlRenderer = (props) => {
    if (props.comparison) {
        //@ts-ignore
        return <ComparisonAttributeSetGridView {...props} currentBenefit={props.scope} leftright={props.side} />;
    }

    //@ts-ignore
    return <AttributeSetGridView {...props} />;
};

const renderCostshareControl: TControlRenderer = (props) => <Costshare {...props} />;

const controlRendererByType: Partial<Record<EControlType, TControlRenderer>> = {
    [EControlType.optionset]: (props) => <OptionsetControl {...props} />,
    [EControlType.boolean]: (props) => <OptionControl {...props} />,
    [EControlType.percent]: (props) => <SliderPercentControl {...props} />,
    [EControlType.date]: (props) => <DatePickerControl {...props} />,
    [EControlType.text]: (props) => <TextFieldControl {...props} />,
    [EControlType.checkBox]: (props) => <Checkbox {...props} />,
    [EControlType.multiText]: (props) => <MultiTextField {...props} />,
    [EControlType.decimal]: (props) => <WholeNumber format={EFormat.float2} {...props} />,
    [EControlType.wholeNumber]: (props) => <WholeNumber format={EFormat.int} zeroAndAbove={true} {...props} />,
    [EControlType.currency]: (props) => <Currency {...props} />,
    [EControlType.message]: (props) => <MessageControl {...props} />,
    [EControlType.lookup]: (props) => <LookupControl {...props} />,
    [EControlType.attributeSet]: renderAttributeSetControl,
    [EControlType.costshare]: renderCostshareControl,
    [EControlType.visionCostshare]: renderCostshareControl,
    [EControlType.pharmacyCostshare]: renderCostshareControl,
    [EControlType.multiOptionset]: (props) => <MultiselectOptionsetControl {...props} />
};

const getControlContent = (props: TControlWrapperTypes): JSX.Element => {
    const renderer = controlRendererByType[props.controlType];

    if (renderer) {
        return renderer(props);
    }

    console.error('Error:No controlType detected/Falling Back to default', props.controlType);
    return <>Error: No controlType detected {props.controlType}</>;
};

const ControlWrapper: React.FunctionComponent<TControlWrapperTypes> = (props) => {
    const theme = useTheme();
    const [locked, setLocked] = useState(Boolean(props.locked));

    useEffect(() => {
        setLocked(Boolean(props.locked));
    }, [props.locked]);

    const handleUnlock = useCallback(() => setLocked(false), []);

    const style = useMemo(() => {
        return getWrapperStyle({
            comparison: props.comparison,
            hasFieldVisibility: Boolean(props.field?.visibility),
            isHighlighted: props.state.status.highlightFieldId === props.configAttribute?.bcbs_config_attributeid,
            themeName: theme.name
        });
    }, [
        props.comparison,
        props.field?.visibility,
        props.state.status.highlightFieldId,
        props.configAttribute?.bcbs_config_attributeid,
        theme.name
    ]);

    if (locked) {
        return <PlaceHolderControl label={props.label} onClick={handleUnlock} />;
    }

    const content = getControlContent(props);
    const diagnosisDebugEnabled = props.state.status.debug.diagnosis;
    const helpText = props.configAttribute?.bcbs_helptext;

    return (
        <div id={props.id} style={style}>
            {diagnosisDebugEnabled &&
                <ControlDebug {...props} />
            }

            {helpText &&
                <FieldInformationCallout
                    helpText={helpText}
                />
            }
            {content}
        </div>
    );
}

interface IFieldInformationCallout {
    helpText: string;
}
const FieldInformationCallout: React.FC<IFieldInformationCallout> = React.memo((props) => {
    return <div style={fieldInformationCalloutContainerStyle}>
        <div style={fieldInformationCalloutPositionStyle}>
            <InformationCallout>
                <div>{props.helpText}</div>
            </InformationCallout>
        </div>
    </div>
});

export default ControlWrapper


